// src/utils/analytics.js
// Centralized analytics — all tracking flows through here.
// Supports GA4, LinkedIn Insight Tag, and Microsoft Clarity.
// To swap providers later, only this file needs to change.

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

// ─── helpers ────────────────────────────────────────────────
const gtag = (...args) => {
  if (typeof window.gtag === 'function') window.gtag(...args);
};

// ─── page views (called by usePageTracking) ─────────────────
export const trackPageView = (path, title) => {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
};

// ─── generic custom event ───────────────────────────────────
export const trackEvent = (eventName, params = {}) => {
  gtag('event', eventName, params);
};

// ─── QR page ────────────────────────────────────────────────
export const trackQRLinkClick = (linkTitle, linkHref) => {
  trackEvent('qr_link_click', {
    link_title: linkTitle,
    link_url: linkHref,
    event_category: 'qr_page',
  });
};

// ─── Contact form ───────────────────────────────────────────
export const trackContactFormSubmit = () => {
  trackEvent('contact_form_submit', {
    event_category: 'conversion',
    event_label: 'contact_page',
  });
};

// ─── Calculator ─────────────────────────────────────────────
export const trackCalculatorStart = (zip) => {
  trackEvent('calculator_start', {
    event_category: 'calculator',
    zip_code: zip,
  });
};

export const trackCalculatorResult = (programs, ttp) => {
  trackEvent('calculator_result', {
    event_category: 'calculator',
    eligible_programs: programs?.join(', ') || 'none',
    ttp_amount: ttp || 0,
  });
};

// ─── Translation Library ────────────────────────────────────
export const trackTranslateHubVisit = (language) => {
  trackEvent('translate_hub_visit', {
    event_category: 'translate',
    language,
  });
};

export const trackTranslateOrgSelect = (orgSlug, language) => {
  trackEvent('translate_org_select', {
    event_category: 'translate',
    org: orgSlug,
    language,
  });
};

export const trackTranslateDocView = (orgSlug, language, docUrl) => {
  trackEvent('translate_doc_view', {
    event_category: 'translate',
    org: orgSlug,
    language,
    doc_url: docUrl,
  });
};

// Whether this device has a voice for the language being read. Fired once per
// document view, so gaps by language/OS/browser show up in GA.
export const trackReadAloudAvailability = (orgSlug, language, platform, voiceName) => {
  trackEvent('read_aloud_availability', {
    event_category: 'translate',
    org: orgSlug,
    language,
    os: platform.os,
    browser: platform.browser,
    voice_found: voiceName ? 'true' : 'false',
    voice_name: voiceName || 'none',
  });
};

export const trackReadAloudStart = (orgSlug, language, mode, voiceName) => {
  trackEvent('read_aloud_start', {
    event_category: 'translate',
    org: orgSlug,
    language,
    mode, // 'page' | 'paragraph'
    voice_name: voiceName || 'none',
  });
};

// ─── Login ──────────────────────────────────────────────────
export const trackLoginAttempt = (success) => {
  trackEvent('login_attempt', {
    event_category: 'auth',
    success: success ? 'true' : 'false',
  });
};

// ─── Navigation / CTA ───────────────────────────────────────
export const trackCTAClick = (ctaName, page) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    cta_name: ctaName,
    page,
  });
};

// ─── Outbound links ─────────────────────────────────────────
export const trackOutboundLink = (url) => {
  trackEvent('outbound_click', {
    event_category: 'outbound',
    link_url: url,
  });
};

// ─── UTM capture ────────────────────────────────────────────
export const captureUTMParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const utm = {};
    let hasUTM = false;

    utmKeys.forEach((key) => {
      const val = params.get(key);
      if (val) {
        utm[key] = val;
        hasUTM = true;
      }
    });

    if (hasUTM) {
      // Persist for the session so conversions carry attribution
      sessionStorage.setItem('hai_utm', JSON.stringify(utm));

      // Set as GA4 user properties for attribution
      if (utm.utm_source) gtag('set', 'user_properties', { traffic_source: utm.utm_source });
      if (utm.utm_medium) gtag('set', 'user_properties', { traffic_medium: utm.utm_medium });
      if (utm.utm_campaign) gtag('set', 'user_properties', { traffic_campaign: utm.utm_campaign });

      // Fire a dedicated event so UTM arrivals are easy to query
      trackEvent('utm_arrival', utm);
    }

    return utm;
  } catch {
    return {};
  }
};

// Retrieve stored UTM (for attaching to conversion events)
export const getStoredUTM = () => {
  try {
    const raw = sessionStorage.getItem('hai_utm');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// ─── Referrer tracking (captures QR vs organic vs direct) ───
export const captureReferrer = () => {
  try {
    const ref = document.referrer;
    const path = window.location.pathname;

    // Tag QR arrivals specifically
    if (path === '/qr') {
      gtag('set', 'user_properties', { entry_point: 'qr_code' });
      trackEvent('qr_scan', { referrer: ref || 'direct' });
    }
  } catch {
    // silent
  }
};

export default {
  trackPageView,
  trackEvent,
  trackQRLinkClick,
  trackContactFormSubmit,
  trackCalculatorStart,
  trackCalculatorResult,
  trackTranslateHubVisit,
  trackTranslateOrgSelect,
  trackTranslateDocView,
  trackReadAloudAvailability,
  trackReadAloudStart,
  trackLoginAttempt,
  trackCTAClick,
  trackOutboundLink,
  captureUTMParams,
  getStoredUTM,
  captureReferrer,
};
